// src/components/Blog/BlogDetail.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkSlug from "remark-slug";
import rehypeRaw from "rehype-raw";
import rehypeAutolinkHeadings from "rehype-autolink-headings";
import { Helmet } from "react-helmet-async";

import blogData from "./blogs/blogData.json";
import BlogLayout from "./BlogLayout";
import LeftRail from "./LeftRail";

export default function BlogDetail() {
  const { slug } = useParams();
  const location = useLocation();
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");
  const articleRef = useRef(null);

  const blog = useMemo(() => blogData.find((b) => b.slug === slug), [slug]);

  const buildAbs = (p) => new URL(p.startsWith("/") ? p : `/${p}`, window.location.origin).toString();

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!blog?.contentUrl) {
        setLoading(false);
        setLoadErr("Article meta not found (missing contentUrl).");
        return;
      }
      try {
        setLoading(true);
        setLoadErr("");

        // Absolute URL for the markdown
        let url = buildAbs(blog.contentUrl);
        if (url.includes("/bolg/")) url = url.replace("/bolg/", "/blogs/");

        // Try primary
        let res = await fetch(url, { cache: "no-cache" });

        // Optional PUBLIC_URL fallback if hosted under a subpath
        if (!res.ok && process.env.PUBLIC_URL) {
          const prefixed = `${process.env.PUBLIC_URL}${blog.contentUrl}`.replace(/\/+/, "/");
          res = await fetch(buildAbs(prefixed), { cache: "no-cache" });
        }

        if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);

        const text = await res.text();
        const stripped = text.replace(/^---[\s\S]*?---\s*/, "");
        if (!cancelled) setContent(stripped);
      } catch (e) {
        console.error("Markdown load failed:", e);
        if (!cancelled) {
          setLoadErr(
            `Failed to load article. Please refresh or check ${blog?.contentUrl || "(no path)"}`
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [blog]);

  // On first load, honor #hash anchors
  useEffect(() => {
    if (!content) return;
    const hash = decodeURIComponent(location.hash || "").replace(/^#/, "");
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (!el) return;
      const headerOffset = 112;
      const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top, behavior: "smooth" });
    }, 60);
    return () => clearTimeout(t);
  }, [content, location.hash]);

  if (!blog) {
    return (
      <BlogLayout
        title="Article not found"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Blog", to: "/blog" },
          { label: "Not found" },
        ]}
      >
        <div className="rounded-xl border bg-yellow-50 text-yellow-800 p-4">
          We couldn’t find that article. Go back to the{" "}
          <Link to="/blog" className="underline">blog list</Link>.
        </div>
      </BlogLayout>
    );
  }

  const metaTitle = blog.meta?.title || `${blog.title} | MyBOQ`;
  const metaDescription = blog.meta?.description || blog.excerpt || "Article from MyBOQ.";
  const ogImage = blog.thumbnail || "/images/blog/placeholder.jpg";

  return (
    <BlogLayout
      title={blog.title}
      breadcrumb={[
        { label: "Home", to: "/" },
        { label: "Blog", to: "/blog" },
        { label: blog.title },
      ]}
      meta={{
        author: blog.author,
        date: blog.date,
        reading_time: blog.reading_time || "7–9 min",
      }}
      leftRail={<LeftRail containerRef={articleRef} title={blog.title} markdown={content} />}
    >
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {blog.thumbnail && (
        <img
          src={blog.thumbnail}
          alt={blog.title}
          className="w-full rounded-2xl border mb-8"
          loading="lazy"
        />
      )}

      {loading ? (
        <div className="space-y-3 animate-pulse">
          <div className="h-6 w-3/4 bg-gray-100 rounded" />
          <div className="h-6 w-1/2 bg-gray-100 rounded" />
          <div className="h-4 w-full bg-gray-100 rounded" />
          <div className="h-4 w-5/6 bg-gray-100 rounded" />
          <div className="h-4 w-2/3 bg-gray-100 rounded" />
        </div>
      ) : loadErr ? (
        <div className="rounded-xl border bg-red-50 text-red-800 p-4">
          {loadErr}
        </div>
      ) : (
        <div
          ref={articleRef}
          className="
            prose prose-zinc lg:prose-lg max-w-none
            prose-p:my-5 prose-p:leading-8
            prose-li:my-2
            prose-ol:my-5 prose-ul:my-5
            prose-blockquote:my-6
            prose-hr:my-10
            prose-img:my-6
            prose-table:my-8
            prose-h2:mt-12 prose-h2:mb-4
            prose-h3:mt-8 prose-h3:mb-3
          "
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkSlug]}
            rehypePlugins={[
              rehypeRaw,
              [rehypeAutolinkHeadings, { behavior: "append", properties: { className: ["no-underline"] } }],
            ]}
            components={{
              table: (props) => (
                <table className="min-w-[720px] w-full border-separate border-spacing-0 overflow-hidden rounded-xl" {...props} />
              ),
              thead: (props) => <thead className="bg-gray-50" {...props} />,
              th: (props) => <th className="text-left text-sm font-semibold text-gray-700 border-b border-gray-200 px-4 py-3" {...props} />,
              td: (props) => <td className="text-sm text-gray-700 border-b border-gray-100 px-4 py-3 align-top" {...props} />,
              tr: (props) => <tr className="odd:bg-white even:bg-gray-50" {...props} />,
              a: (props) => <a {...props} className="text-orange-600 hover:text-orange-700 underline underline-offset-2" />,
              img: ({ src, alt, ...props }) => (
                <img src={src} alt={alt || ""} loading="lazy" className="rounded-xl border" {...props} />
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        </div>
      )}

      <div className="mt-12 rounded-2xl border bg-gradient-to-r from-gray-50 to-white p-6 md:p-8">
        <div className="md:flex md:items-center md:justify-between gap-6">
          <div>
            <h3 className="text-xl font-bold">Get a bank-ready BOQ today</h3>
            <p className="text-gray-600 mt-1">
              Use MyBOQ to generate SSR-aligned estimates and stage-wise summaries in minutes.
            </p>
          </div>
          <Link
            to="/signup"
            className="mt-4 md:mt-0 inline-flex items-center rounded-full bg-orange-600 px-6 py-3 font-semibold text-white hover:bg-orange-700"
          >
            Try MyBOQ Demo →
          </Link>
        </div>
      </div>
    </BlogLayout>
  );
}
